/**
 * Utility functions for mid-cycle join validation
 * Mirrors backend logic in server/src/utility/midCycleJoinUtils.ts
 *
 * @format
 */

const END_OF_MONTH_WINDOW_DAYS = 7;

/**
 * Last day of `date`'s month and the first day of its end-of-month window, as
 * UTC calendar days (the server's isEndOfMonthWindow uses UTC days too, so the
 * UI never says "open" while the server would reject the request).
 */
const getWindowBounds = (date: Date) => {
	const year = date.getUTCFullYear();
	const month = date.getUTCMonth();
	const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
	const windowStartDay = lastDay - END_OF_MONTH_WINDOW_DAYS + 1;
	return {
		windowStart: new Date(Date.UTC(year, month, windowStartDay)),
		windowEnd: new Date(Date.UTC(year, month, lastDay)),
		windowStartDay,
		lastDay,
	};
};

/**
 * Check if current date is within the end-of-month window
 * Members can only submit join/add requests during the last 7 days of the month
 * while a contribution cycle is in progress.
 */
export const isEndOfMonthWindow = (now: Date = new Date()): boolean => {
	const { windowStartDay, lastDay } = getWindowBounds(now);
	const day = now.getUTCDate();
	return day >= windowStartDay && day <= lastDay;
};

/**
 * Get the date range for the current end-of-month window
 * Returns null if not currently in the window
 */
export const getEndOfMonthWindowDates = (
	now: Date = new Date(),
): {
	startDate: Date;
	endDate: Date;
} | null => {
	if (!isEndOfMonthWindow(now)) return null;
	const { windowStart, windowEnd } = getWindowBounds(now);
	return { startDate: windowStart, endDate: windowEnd };
};

/**
 * Get a user-friendly message explaining when join requests can be submitted.
 * `cycleInProgress` must be true only for a cycle that has actually started
 * (see isCycleInProgress); scheduled cycles don't restrict joining.
 */
export const getJoinWindowMessage = (
	cycleInProgress: boolean,
	now: Date = new Date(),
): string => {
	if (!cycleInProgress) {
		return "Join this circle";
	}

	if (isEndOfMonthWindow(now)) {
		return "Join requests open - Last 7 days of month!";
	}

	const { windowStart } = getWindowBounds(now);
	const label = windowStart.toLocaleDateString("en-US", {
		month: "long",
		day: "numeric",
		timeZone: "UTC",
	});

	return `Join requests open ${label}`;
};

/**
 * Format a date for display
 */
export const formatDateForDisplay = (date: Date): string => {
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
};

type CycleDates = {
	startDate?: string | Date | null;
	endDate?: string | Date | null;
	status?: string;
	timezone?: string | null;
};

const DEFAULT_CYCLE_TIMEZONE = "America/Chicago";

const getDateStringInTimezone = (date: Date, timeZone: string): string => {
	try {
		return new Intl.DateTimeFormat("en-CA", {
			timeZone,
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		}).format(date);
	} catch {
		return date.toISOString().split("T")[0];
	}
};

/**
 * A cycle is "in progress" once it is active, its start day has arrived in the
 * cycle's timezone, and its end date has not passed. Cycles scheduled for the
 * future do not count.
 * Mirrors backend hasCycleStarted in server/src/utility/midCycleJoinUtils.ts
 */
export const isCycleInProgress = (
	cycle: CycleDates | null | undefined,
	now: Date = new Date(),
): boolean => {
	if (!cycle?.startDate || !cycle?.endDate) return false;
	if (cycle.status && cycle.status !== "active") return false;

	const startDate = new Date(cycle.startDate);
	const endDate = new Date(cycle.endDate);
	if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return false;

	// startDate is stored as UTC midnight of the cycle's calendar start day
	const startDay = startDate.toISOString().split("T")[0];
	const today = getDateStringInTimezone(
		now,
		cycle.timezone || DEFAULT_CYCLE_TIMEZONE,
	);

	return today >= startDay && now.getTime() <= endDate.getTime();
};
